// ABOUTME: Credentials step for the Amazon OAuth finalize modal. Auto-fills description
// ABOUTME: from the picked marketplace; renders the AmazonOAuthButton in already-connected state.

import React, { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import Text from '../../../../theme/text/Text';
import { tokens } from '../../../../../theme';
import { setTabErrors, updateData } from '../../../../../newapi/global/progressionTabSlice';
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import EMGeneralExplainerBox from '../../../ModalsPopover/explainers/EMGeneralExplainerBox';
import { InputDropdown } from '../../../../theme/dropdowns/InputDropdown';
import { useGetRelationsSelectQuery } from '../../../../../newapi/global/relation/relationSlice';
import AmazonOAuthButton from '../../MpAddWebshop/components/AmazonOAuthButton';
import { config } from '../../../../../newapi/apiUrlConfig';

const MARKETPLACE_OPTIONS = [
    { value: 'NL', label: 'Netherlands (NL)', marketplaceId: 'A1805IZSGTT6HS' },
    { value: 'BE', label: 'Belgium (BE)', marketplaceId: 'AMEN7PMS3EDWL' },
    { value: 'DE', label: 'Germany (DE)', marketplaceId: 'A1PA6795UKMFR9' },
    { value: 'FR', label: 'France (FR)', marketplaceId: 'A13V1IB3VIYBER' },
    { value: 'IT', label: 'Italy (IT)', marketplaceId: 'APJ6JRA9NG5V4' },
    { value: 'ES', label: 'Spain (ES)', marketplaceId: 'A1RKKUPIHCS9HS' },
    { value: 'UK', label: 'United Kingdom (UK)', marketplaceId: 'A1F83G8C2ARO7P' },
    { value: 'SE', label: 'Sweden (SE)', marketplaceId: 'A2NODRKZP88ZB9' },
    { value: 'PL', label: 'Poland (PL)', marketplaceId: 'A1C3SOZRARQ6R3' },
    { value: 'IE', label: 'Ireland (IE)', marketplaceId: 'A28R8C7NBKEWEA' },
];

const AUTO_DESCRIPTION_PREFIX = 'Amazon ';

const Content = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (initialized) return;
        const refresh_token = props.refreshToken;
        const selling_partner_id = props.sellingPartnerId;
        if (!refresh_token || !selling_partner_id) return;

        const seedDescription = `${AUTO_DESCRIPTION_PREFIX}NL`;
        dispatch(updateData({
            webshop_type: 'amazon_stockhub',
            flow_type: 'standard',
            description: seedDescription,
            shippingName: '',
            credentials: {
                refresh_token,
                selling_partner_id,
                marketplace: 'NL',
                marketplace_id: 'A1805IZSGTT6HS',
            },
        }));
        setInitialized(true);
    }, [initialized, dispatch, props.refreshToken, props.sellingPartnerId]);

    const handleInput = (key, val) => {
        props.handleDataUpdate({ [key]: val });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    const handleMarketplaceChange = (selectedIndex) => {
        const option = MARKETPLACE_OPTIONS[selectedIndex];
        const currentDescription = data.description ?? '';
        const isAutoDescription = currentDescription === '' ||
            (currentDescription.startsWith(AUTO_DESCRIPTION_PREFIX) &&
             MARKETPLACE_OPTIONS.some(o => `${AUTO_DESCRIPTION_PREFIX}${o.value}` === currentDescription));

        props.handleDataUpdate({
            ...(isAutoDescription ? { description: `${AUTO_DESCRIPTION_PREFIX}${option.value}` } : {}),
            credentials: {
                ...(data.credentials ?? {}),
                marketplace: option.value,
                marketplace_id: option.marketplaceId,
            },
        });
    };

    const handleAmazonOAuthDisconnect = () => {
        const next = { ...(data.credentials ?? {}) };
        delete next.refresh_token;
        delete next.selling_partner_id;
        delete next.marketplace;
        delete next.marketplace_id;
        props.handleDataUpdate({ credentials: next });
    };

    const isAuthenticated = !!data.credentials?.refresh_token;

    const amazonOAuthUrl = `${config.url.API_BACKEND_URL}oauth/amazon/start`;
    const amazonBackendOrigin = new URL(config.url.API_BACKEND_URL).origin;

    const { data: relationsData } = useGetRelationsSelectQuery();
    const marketplaceIndex = MARKETPLACE_OPTIONS.findIndex(
        o => o.value === (data.credentials?.marketplace || 'NL'),
    );

    return (
        <div>
            {isFulfilment ? (
                <Box sx={{
                    width: 1, display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 7,
                }}>
                    <Box sx={{ width: 120 }}>
                        <Text>{t('relation')}</Text>
                    </Box>
                    <Box sx={{
                        flex: 1,
                        backgroundColor: `${theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[100]}`,
                        borderRadius: 4, display: 'flex',
                    }}>
                        <ShValidatedInputDropdown
                            closeOnSelection
                            searchOptions={{ enabled: true, placeholder: t('search_relation_placeholder') }}
                            name="relationId"
                            height={40}
                            displayName="name"
                            changeField="id"
                            selected={relationsData?.findIndex(r => r.id === data?.relationId) ?? 0}
                            noSelection={t('choose_relation')}
                            options={relationsData ?? []}
                            onChange={handleInput}
                            error={props.errors.relationId}
                        />
                    </Box>
                </Box>
            ) : null}

            <Box sx={{
                width: 1, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 7, mb: 1.5,
                ...(isFulfilment ? { paddingTop: 1.5 } : {}),
            }}>
                <Box sx={{ width: 120 }}>
                    <Text>{t('description')}</Text>
                </Box>
                <Box sx={{
                    flex: 1,
                    backgroundColor: `${theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[100]}`,
                    borderRadius: 4, display: 'flex',
                }}>
                    <ShValidatedInput
                        name="description"
                        value={data.description ?? ''}
                        onChange={handleInput}
                        error={props.errors.description}
                    />
                </Box>
            </Box>

            <Box sx={{
                width: 1, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 7,
            }}>
                <Box sx={{ width: 120, display: 'flex', alignItems: 'center' }}>
                    <Text>{t('shipping_name')}</Text>
                    <EMGeneralExplainerBox title={t('shipping_name')} text={t('em_shippingname_text')} />
                </Box>
                <Box sx={{
                    flex: 1,
                    backgroundColor: `${theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[100]}`,
                    borderRadius: 4, display: 'flex',
                }}>
                    <ShValidatedInput
                        name="shippingName"
                        value={data.shippingName ?? ''}
                        onChange={handleInput}
                        error={props.errors.shippingName}
                    />
                </Box>
            </Box>

            <Box sx={{ width: 1, mt: 2 }}>
                <AmazonOAuthButton
                    oauthUrl={amazonOAuthUrl}
                    backendOrigin={amazonBackendOrigin}
                    onSuccess={() => {}}
                    onError={() => {}}
                    isAuthenticated={isAuthenticated}
                    onDisconnect={handleAmazonOAuthDisconnect}
                />

                <Box sx={{
                    width: 1, display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 7, mt: 2,
                }}>
                    <Box sx={{ width: 120 }}>
                        <Text>{t('marketplace')}</Text>
                    </Box>
                    <Box sx={{
                        flex: 1,
                        backgroundColor: `${theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[100]}`,
                        borderRadius: 4, display: 'flex',
                    }}>
                        <InputDropdown
                            name="marketplace"
                            noSelection={t('choose_option')}
                            displayName="label"
                            options={MARKETPLACE_OPTIONS}
                            selected={marketplaceIndex}
                            onChange={handleMarketplaceChange}
                            closeOnSelection
                        />
                    </Box>
                </Box>
            </Box>
        </div>
    );
};

const Credentials = (props) => {
    const { t } = useTranslation();
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

    const conditionalSchema = [
        { key: 'description', errorField: t('description'), alwaysRequired: true, type: 'text' },
        {
            key: 'relationId',
            errorField: t('relation'),
            alwaysRequired: isFulfilment,
            condition: () => false,
            type: 'integer',
        },
    ];

    return (
        <ProgressionTab
            tabId="tabCredentials"
            subtitle={t('webshopcredentials')}
            step={1}
            nextTab="tabSync"
            maxSteps={2}
            conditionalSchema={conditionalSchema}
        >
            <Content refreshToken={props.refreshToken} sellingPartnerId={props.sellingPartnerId} />
        </ProgressionTab>
    );
};

export default Credentials;
